import { IconArrowUp } from '@tabler/icons-react'
import { ComponentProps, KeyboardEvent, ReactNode, RefObject, useRef } from 'react'

import { AstraButton, AstraIcon, astraTwMerge, InputBase, inputTypographyStyles, SizeOptions } from '~/astra'

type Props = {
    onChange: (value: string) => void
    size?: SizeOptions
    isValid?: (value: string) => boolean
    onSendMessage?: (value: string) => void
    bottomElement?: ReactNode
    ref?: RefObject<HTMLTextAreaElement | null>
    value?: string
} & InputBase &
    Omit<ComponentProps<'textarea'>, 'defaultValue' | 'onClick' | 'onChange'>

export const ChatInput = ({
    bottomElement,
    onChange,
    isValid,
    size = 'medium',
    disabled,
    readOnly,
    onSendMessage,
    name,
    placeholder,
    ref,
    value,
    ...rest
}: Props) => {
    const textAreaRef = useRef<HTMLTextAreaElement>(null)

    const handleSendMessage = (value: string) => {
        onSendMessage?.(value)
        onChange('')
    }
    const handleKeyPress = (event: KeyboardEvent<HTMLTextAreaElement>) => {
        if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault()
            const resolvedValue = event.currentTarget.value.trim()

            if (!resolvedValue || (isValid && !isValid(resolvedValue))) {
                return
            }

            handleSendMessage(resolvedValue)
        }
        event.stopPropagation()
    }

    const handleButtonClick = () => {
        if (!value || (isValid && !isValid(value))) {
            return
        }
        handleSendMessage(value)
    }

    return (
        <div className="flex flex-col gap-2 rounded-md border border-base p-2 text-muted outline outline-4 outline-purple-300/25">
            <textarea
                ref={currRef => {
                    textAreaRef.current = currRef
                    if (ref) {
                        ref.current = currRef
                    }
                }}
                name={name}
                disabled={disabled}
                readOnly={readOnly}
                aria-label={name}
                className={`w-full resize-none self-center overflow-y-hidden bg-transparent outline-none ${inputTypographyStyles[size]}`}
                onChange={e => {
                    onChange(e.target.value)
                }}
                value={value}
                placeholder={placeholder}
                onKeyDown={handleKeyPress}
                {...rest}
            />
            <div className={astraTwMerge('flex items-center', bottomElement ? 'justify-between' : 'justify-end')}>
                {bottomElement}
                <AstraButton
                    disabled={!value || value.trim().length < 1 || disabled || readOnly}
                    icon={<AstraIcon icon={IconArrowUp} />}
                    size="large"
                    onClick={handleButtonClick}
                />
            </div>
        </div>
    )
}
